import { Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Breadcrumbs, Button, Link, Paper, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonsContainer, ContentContainer, FormContainer, LogoPreview, PageContainer, PreviewContainer, ResumoPaper } from "./styles";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useTema } from "../../hooks/useTema";
import { ITema } from "../../../core/interfaces/ITema";
import { IImagemTema } from "../../../core/interfaces/IImagemTema";
import { ExpandMore } from "@mui/icons-material";
import { HexColorPicker } from "react-colorful";
import { CorInput } from "../../../core/components/CorInput";
import { DropzoneUpload } from "../../../core/components/DropzoneUpload";
import { MIMETypes } from "../../../core/enum/MIMETypes";
import { useThemeContext } from "../../../core/contexts/ThemeContext";
import { LightTheme } from "../../../core/themes/light";
import { DarkTheme } from "../../../core/themes/dark";
import FullScreenLoading from "../../../core/components/FullScreenLoading";

export const Tema = () => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const { uploadImagem, salvarTema, buscarTema, limparTema, loading } = useTema();
    const { refreshTemas } = useThemeContext();

    const [tabAtiva, setTabAtiva] = useState<"light" | "dark">("light");
    const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
    
    // Light theme state
    const [lightLogo, setLightLogo] = useState<File | IImagemTema | undefined>(undefined);
    const [lightCorPrimaria, setLightCorPrimaria] = useState<string>("#1976d2");
    const [lightCorSecundaria, setLightCorSecundaria] = useState<string>("#da4167");
    const [lightTemaUuid, setLightTemaUuid] = useState<string | undefined>(undefined);

    // Dark theme state
    const [darkLogo, setDarkLogo] = useState<File | IImagemTema | undefined>(undefined);
    const [darkCorPrimaria, setDarkCorPrimaria] = useState<string>("#90caf9");
    const [darkCorSecundaria, setDarkCorSecundaria] = useState<string>("#da4167");
    const [darkTemaUuid, setDarkTemaUuid] = useState<string | undefined>(undefined);

    useEffect(() => {
        carregarTemas();
    }, []);

    const carregarTemas = async () => {
        // Carregar tema light
        const temaLight = await buscarTema("light");
        if (temaLight) {
            setLightTemaUuid(temaLight.uuid);
            setLightCorPrimaria(temaLight.materialUi.palette.primary.main);
            setLightCorSecundaria(temaLight.materialUi.palette.secondary.main);
            const logo = temaLight.imagens?.find(img => img.tipo === "Logo");
            if (logo) {
                setLightLogo(logo);
            }
        } else {
            // Set defaults from LightTheme
            setLightCorPrimaria(LightTheme.palette.primary.main);
            setLightCorSecundaria(LightTheme.palette.secondary.main);
        }

        // Carregar tema dark
        const temaDark = await buscarTema("dark");
        if (temaDark) {
            setDarkTemaUuid(temaDark.uuid);
            setDarkCorPrimaria(temaDark.materialUi.palette.primary.main);
            setDarkCorSecundaria(temaDark.materialUi.palette.secondary.main);
            const logo = temaDark.imagens?.find(img => img.tipo === "Logo");
            if (logo) {
                setDarkLogo(logo);
            }
        } else {
            // Set defaults from DarkTheme
            setDarkCorPrimaria(DarkTheme.palette.primary.main);
            setDarkCorSecundaria(DarkTheme.palette.secondary.main);
        }
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: "light" | "dark") => {
        setTabAtiva(newValue);
    };

    const handleUploadLogo = (arquivos: File[]) => {
        if (arquivos.length > 0) {
            if (tabAtiva === "light") {
                setLightLogo(arquivos[0]);
            } else {
                setDarkLogo(arquivos[0]);
            }
        }
    };

    const handleRemoverLogo = () => {
        if (tabAtiva === "light") {
            setLightLogo(undefined);
        } else {
            setDarkLogo(undefined);
        }
    };

    const handleSalvar = async () => {
        const modo = tabAtiva;
        const corPrimaria = modo === "light" ? lightCorPrimaria : darkCorPrimaria;
        const corSecundaria = modo === "light" ? lightCorSecundaria : darkCorSecundaria;
        const logo = modo === "light" ? lightLogo : darkLogo;
        const temaUuid = modo === "light" ? lightTemaUuid : darkTemaUuid;

        let imagemTema: IImagemTema | undefined = undefined;

        // Upload logo if it's a File
        if (logo && logo instanceof File) {
            const resultado = await uploadImagem(logo, "Logo");
            if (!resultado) {
                return snackbar("Erro ao fazer upload da logo", { severity: "error" });
            }
            imagemTema = resultado;
        } else if (logo && 'uuid' in logo) {
            imagemTema = logo as IImagemTema;
        }

        // Get base theme configuration
        const baseThemeConfig = modo === "light" ? LightTheme : DarkTheme;

        // Build tema object matching IMaterialUi interface
        const tema: Partial<ITema> = {
            uuid: temaUuid,
            modo: modo,
            materialUi: {
                typography: {
                    fontFamily: baseThemeConfig.typography.fontFamily || 'Poppins, sans-serif',
                },
                palette: {
                    primary: {
                        main: corPrimaria,
                    },
                    secondary: {
                        main: corSecundaria,
                    },
                    background: {
                        default: baseThemeConfig.palette.background.default,
                        paper: baseThemeConfig.palette.background.paper,
                    },
                    text: {
                        primary: baseThemeConfig.palette.text.primary,
                        secondary: baseThemeConfig.palette.text.secondary,
                        disabled: baseThemeConfig.palette.text.disabled || '#999',
                    },
                    action: {
                        active: baseThemeConfig.palette.action.active || '#333',
                        hover: baseThemeConfig.palette.action.hover || '#f5f5f5',
                    },
                    common: {
                        white: baseThemeConfig.palette.common.white,
                    },
                    success: {
                        main: baseThemeConfig.palette.success.main,
                    },
                    error: {
                        main: baseThemeConfig.palette.error.main,
                    },
                    divider: baseThemeConfig.palette.divider || '#e0e0e0',
                },
            },
            imagens: imagemTema ? [imagemTema] : [],
        };

        const resultado = await salvarTema(tema);
        if (!resultado) {
            return snackbar("Erro ao salvar tema", { severity: "error" });
        }

        snackbar("Tema salvo com sucesso", { severity: "success" });
        
        // Show full screen loading and reload after 5 seconds
        setShowFullScreenLoading(true);
        setTimeout(() => {
            window.location.reload();
        }, 5000);
    };

    const handleLimparTema = async () => {
        const modo = tabAtiva;

        const resultado = await limparTema(modo);
        if (!resultado) {
            return snackbar("Erro ao limpar tema", { severity: "error" });
        }

        snackbar("Tema limpo com sucesso", { severity: "success" });
        
        // Show full screen loading and reload after 5 seconds
        setShowFullScreenLoading(true);
        setTimeout(() => {
            window.location.reload();
        }, 5000);
    };

    const corPrimariaAtual = tabAtiva === "light" ? lightCorPrimaria : darkCorPrimaria;
    const corSecundariaAtual = tabAtiva === "light" ? lightCorSecundaria : darkCorSecundaria;
    const logoAtual = tabAtiva === "light" ? lightLogo : darkLogo;

    const setCorPrimariaAtual = (cor: string) => {
        if (tabAtiva === "light") {
            setLightCorPrimaria(cor);
        } else {
            setDarkCorPrimaria(cor);
        }
    };

    const setCorSecundariaAtual = (cor: string) => {
        if (tabAtiva === "light") {
            setLightCorSecundaria(cor);
        } else {
            setDarkCorSecundaria(cor);
        }
    };

    const getLogoUrl = () => {
        if (!logoAtual) return undefined;
        if (logoAtual instanceof File) {
            return URL.createObjectURL(logoAtual);
        }
        if ('url' in logoAtual) {
            return logoAtual.url;
        }
        return undefined;
    };

    return (
        <PageContainer>
            {showFullScreenLoading && <FullScreenLoading />}
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="primary" href="/configuracoes">
                    Configurações
                </Link>
                <Typography color="text.primary">Tema</Typography>
            </Breadcrumbs>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginTop: "2rem" }}>
                <Tabs value={tabAtiva} onChange={handleTabChange}>
                    <Tab label="Tema Claro" value="light" />
                    <Tab label="Tema Escuro" value="dark" />
                </Tabs>
            </Box>

            <ContentContainer>
                <FormContainer>
                    <Accordion
                        expanded={true}
                        sx={{
                            "&:before": {
                                display: "none",
                            },
                            "&.MuiAccordion-root::before": {
                                display: "none",
                            },
                            borderRadius: "16px",
                        }}
                    >
                        <AccordionSummary>
                            <Typography variant="h6">Logo</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ minHeight: '250px', height: '250px' }}>
                                    <DropzoneUpload
                                        mimetypes={[MIMETypes["image/jpeg"], MIMETypes["image/jpg"], MIMETypes["image/png"]]}
                                        limiteArquivos={1}
                                        onDrop={handleUploadLogo}
                                        descricao="Arraste a logo aqui ou clique para selecionar"
                                        tamanhoMaximoMB={5}
                                    />
                                </Box>
                                {logoAtual && (
                                    <Box>
                                        <LogoPreview>
                                            <img src={getLogoUrl()} alt="Logo preview" />
                                        </LogoPreview>
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            onClick={handleRemoverLogo}
                                            fullWidth
                                        >
                                            Remover Logo
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={true}
                        sx={{
                            "&:before": {
                                display: "none",
                            },
                            "&.MuiAccordion-root::before": {
                                display: "none",
                            },
                            borderRadius: "16px",
                        }}
                    >
                        <AccordionSummary>
                            <Typography variant="h6">Cor Primária</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
                            <HexColorPicker color={corPrimariaAtual} onChange={setCorPrimariaAtual} />
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <CorInput
                                    label="Cor primária"
                                    cor={corPrimariaAtual}
                                    mensagemCopiar="Cor primária copiada para a área de transferência"
                                    onChange={setCorPrimariaAtual}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={true}
                        sx={{
                            "&:before": {
                                display: "none",
                            },
                            "&.MuiAccordion-root::before": {
                                display: "none",
                            },
                            borderRadius: "16px",
                        }}
                    >
                        <AccordionSummary>
                            <Typography variant="h6">Cor Secundária</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
                            <HexColorPicker color={corSecundariaAtual} onChange={setCorSecundariaAtual} />
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <CorInput
                                    label="Cor secundária"
                                    cor={corSecundariaAtual}
                                    mensagemCopiar="Cor secundária copiada para a área de transferência"
                                    onChange={setCorSecundariaAtual}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Alert severity="info">
                        <AlertTitle>Informação</AlertTitle>
                        As cores selecionadas serão aplicadas em todo o sistema. 
                        A cor primária é usada em botões principais e elementos de destaque. 
                        A cor secundária é usada em elementos secundários e de suporte.
                    </Alert>
                </FormContainer>

                <ResumoPaper>
                    <Typography variant="h6">Resumo</Typography>

                    <PreviewContainer sx={{ marginTop: "2rem" }}>
                        <Typography variant="subtitle2" fontWeight="bold">Preview</Typography>
                        
                        {logoAtual && (
                            <LogoPreview>
                                <img src={getLogoUrl()} alt="Logo preview" />
                            </LogoPreview>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <CorInput
                                label="Cor primária"
                                cor={corPrimariaAtual}
                                mensagemCopiar="Cor primária copiada"
                            />
                            <CorInput
                                label="Cor secundária"
                                cor={corSecundariaAtual}
                                mensagemCopiar="Cor secundária copiada"
                            />
                        </Box>
                    </PreviewContainer>

                    <ButtonsContainer>
                        <Button 
                            size="large" 
                            variant="outlined" 
                            color="error"
                            onClick={handleLimparTema}
                            disabled={loading || showFullScreenLoading}
                        >
                            Limpar
                        </Button>
                        <Button 
                            size="large" 
                            variant="contained" 
                            onClick={handleSalvar}
                            disabled={loading || showFullScreenLoading}
                        >
                            Salvar
                        </Button>
                    </ButtonsContainer>
                </ResumoPaper>
            </ContentContainer>
        </PageContainer>
    );
};

